import React, { useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { formatArea, totalArea } from "./locationArea";

export const CleaningInfoVisibility = ({ show = true, onChange = () => {}, speed = null, onSpeedChange = () => {} }) => (
  <div className="px-3 pb-3 text-slate-200">
    <div className="rounded-lg border border-slate-700 bg-slate-950/40">
      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
        <div>
          <div className="text-[0.7rem] font-semibold text-slate-300">Cleaning data</div>
          <div className="mt-0.5 text-[0.65rem] text-slate-500">{show ? "Show" : "Hide"}</div>
        </div>
        <label className="ml-auto flex items-center gap-1 text-[0.65rem] text-slate-400" title="Default cleaning speed used for automatic group cleaning times">
          <span>Cleaning speed</span>
          <input
            key={speed ?? "none"}
            type="number" min="10" max="5000" step="10"
            aria-label="Cleaning speed in square metres per hour"
            defaultValue={speed ?? ""}
            onBlur={(event) => {
              const value = Number(event.target.value);
              if (value > 0 && value !== speed) onSpeedChange(value);
            }}
            onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
            className="w-14 rounded border border-slate-600 bg-slate-900 px-1.5 py-0.5 text-slate-100 outline-none focus:border-teal-500"
          />
          <span>m²/h</span>
        </label>
        <button
          type="button"
          role="switch"
          aria-label="Show cleaning data"
          aria-checked={show}
          onClick={() => onChange(!show)}
          className={`relative h-5 w-9 shrink-0 rounded-full border transition-colors ${
            show
              ? "border-teal-400 bg-teal-500"
              : "border-slate-600 bg-slate-800"
          }`}
        >
          <span
            className={`absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full bg-white shadow-sm transition-all ${
              show ? "right-0.5" : "left-0.5"
            }`}
          />
        </button>
      </div>
    </div>
  </div>
);

// Cleaning groupok listája. A szerkesztett group locationjeit a térképen kattintva
// lehet be- és kivenni; a lista itt csak megjeleníti és törölhetővé teszi őket.
const IcleanViewWashing = ({
  rects = [],
  groups = [],
  editingGroupId = null,
  onCreate = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onRemoveLocation = () => {},
  onRename = () => {},
  onDurationChange = () => {},
  locationAreaById = new Map(),
}) => {
  const [renamingGroupId, setRenamingGroupId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [durationGroupId, setDurationGroupId] = useState(null);
  const [draftHours, setDraftHours] = useState("");

  const finishDuration = (group, save) => {
    if (save && String(durationGroupId) === String(group.id)) {
      if (String(draftHours).trim() === "") {
        // Üres mező: vissza az automatikus időre (terület ÷ takarítási sebesség).
        if (group.duration_manual) onDurationChange(group, null);
      } else {
        const minutes = Math.round(parseFloat(draftHours) * 60);
        if (minutes > 0 && (minutes !== group.duration_minutes || !group.duration_manual)) onDurationChange(group, minutes);
      }
    }
    setDurationGroupId(null);
  };

  const startRename = (group) => {
    setRenamingGroupId(group.id);
    setDraftName(group.name);
  };

  const finishRename = (group, save) => {
    // Escape után a mező eltűnésekor érkező blur már ne mentsen.
    if (save && String(renamingGroupId) === String(group.id)) onRename(group, draftName);
    setRenamingGroupId(null);
  };

  const locationName = (locationId) => {
    const location = rects.find((item) => String(item.id) === String(locationId));
    return location?.name || `Location #${locationId}`;
  };

  return (
    <div className="px-3 pt-3 pb-3 text-slate-200">
      <div className="rounded-lg border border-slate-700 bg-slate-950/40">
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="text-[0.7rem] font-semibold text-slate-300">Cleaning groups</div>
          <button
            type="button"
            aria-label="New cleaning group"
            title="New cleaning group"
            onClick={onCreate}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-600 text-slate-300 transition-colors hover:border-teal-400 hover:text-teal-300"
          >
            <Plus size={15} strokeWidth={2} />
          </button>
        </div>

        {groups.length === 0 && (
          <div className="border-t border-slate-800 px-3 py-2 text-xs text-slate-500">No cleaning groups yet.</div>
        )}

        {groups.map((group) => {
          const editing = String(group.id) === String(editingGroupId);
          return (
            <div key={group.id} className={`border-t border-slate-800 ${editing ? "bg-teal-500/5" : ""}`}>
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  {String(renamingGroupId) === String(group.id) ? (
                    <input
                      autoFocus
                      aria-label={`Name of ${group.name}`}
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                      onBlur={() => finishRename(group, true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                        if (event.key === "Escape") finishRename(group, false);
                      }}
                      maxLength={120}
                      className="w-full rounded border border-teal-500 bg-slate-900 px-1.5 py-0.5 text-xs text-slate-100 outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      title="Rename"
                      onClick={() => startRename(group)}
                      className="block max-w-full truncate text-left text-xs text-slate-200 hover:text-teal-300"
                    >
                      {group.name}
                    </button>
                  )}
                  <div className="text-[0.65rem] text-slate-500">
                    {group.location_ids.length} locations
                    {" · "}
                    <span className="tabular-nums" title="Total area of the group's locations">
                      {formatArea(totalArea(group.location_ids, rects, locationAreaById)) || "area: set the root area"}
                    </span>
                  </div>
                  <label className="mt-1 flex items-center gap-1.5 text-[0.65rem] text-slate-400">
                    <span>Cleaning time</span>
                    <input
                      type="number" min="0.25" max="24" step="0.25"
                      aria-label={`Cleaning time of ${group.name} in hours`}
                      value={String(durationGroupId) === String(group.id) ? draftHours : ((group.duration_minutes || 60) / 60).toFixed(2).replace(/\.?0+$/, "")}
                      onFocus={() => { setDurationGroupId(group.id); setDraftHours(((group.duration_minutes || 60) / 60).toFixed(2).replace(/\.?0+$/, "")); }}
                      onChange={(event) => setDraftHours(event.target.value)}
                      onBlur={() => finishDuration(group, true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                        if (event.key === "Escape") finishDuration(group, false);
                      }}
                      className="w-14 rounded border border-slate-600 bg-slate-900 px-1.5 py-0.5 text-slate-100 outline-none focus:border-teal-500"
                    />
                    <span>h</span>
                    {!group.duration_manual && <span className="rounded bg-slate-800 px-1 text-[0.6rem] text-teal-300" title="Calculated from the group area and the cleaning speed; type a value to override, clear it to go back">auto</span>}
                  </label>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    aria-label={`${editing ? "Finish editing" : "Edit"} ${group.name}`}
                    aria-pressed={editing}
                    title={editing ? "Finish editing" : "Edit"}
                    onClick={() => onEdit(editing ? null : group.id)}
                    className={`flex h-7 w-7 items-center justify-center rounded-md border transition-colors ${
                      editing
                        ? "border-teal-400 bg-teal-400 text-slate-950"
                        : "border-slate-600 text-slate-300 hover:border-teal-400 hover:text-teal-300"
                    }`}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${group.name}`}
                    title="Delete"
                    onClick={() => onDelete(group)}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-600 text-slate-400 transition-colors hover:border-red-400 hover:text-red-400"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {editing && (
                <div className="px-2 pb-2">
                  <div className="mb-1.5 px-1 text-[0.65rem] text-teal-300">Click locations on the map to add or remove them.</div>
                  {group.location_ids.length > 0 ? (
                    <div className="space-y-1">
                      {group.location_ids.map((locationId) => (
                        <div
                          key={locationId}
                          className="flex items-center justify-between rounded bg-slate-800/70 px-2 py-1.5 text-xs"
                        >
                          <span className="min-w-0 truncate">{locationName(locationId)}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${locationName(locationId)}`}
                            title="Remove"
                            onClick={() => onRemoveLocation(group, locationId)}
                            className="ml-2 shrink-0 text-slate-500 hover:text-red-400"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-1 py-1 text-xs text-slate-500">No locations in this group yet.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IcleanViewWashing;
